import styled from "@emotion/styled/macro";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Modal,
  SvgIcon,
  SvgNames,
  TABLET_BREAKPOINT,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import type { ReviewOfferProduct } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { getTitleTabScrollLeft } from "utils/productCarouselTabScroll";
import {
  getReviewOfferProductsIdentityKey,
  getSimplifiedOfferReviewCarouselDefaultIndex,
  sortSimplifiedOfferReviewProducts,
} from "utils/sortProducts";
import { getSimplifiedProductTabLabel } from "utils/simplifiedProductCarouselLabels";

const useSimplifiedProductCarousel = (products: ReviewOfferProduct[]) => {
  const productsIdentityKey = getReviewOfferProductsIdentityKey(products);

  const sortedProducts = useMemo(
    () => sortSimplifiedOfferReviewProducts(products),
    [products]
  );

  const [currentIndex, setCurrentIndex] = useState(() =>
    getSimplifiedOfferReviewCarouselDefaultIndex(
      sortSimplifiedOfferReviewProducts(products)
    )
  );

  const previousProductsIdentityKeyRef = useRef(productsIdentityKey);

  useEffect(() => {
    if (previousProductsIdentityKeyRef.current === productsIdentityKey) {
      return;
    }

    previousProductsIdentityKeyRef.current = productsIdentityKey;
    setCurrentIndex(
      getSimplifiedOfferReviewCarouselDefaultIndex(sortedProducts)
    );
  }, [productsIdentityKey, sortedProducts]);

  useEffect(() => {
    if (currentIndex >= sortedProducts.length) {
      setCurrentIndex(
        getSimplifiedOfferReviewCarouselDefaultIndex(sortedProducts)
      );
    }
  }, [currentIndex, sortedProducts]);

  return { sortedProducts, currentIndex, setCurrentIndex };
};

type SimplifiedProductCarouselTabsProps = {
  sortedProducts: ReviewOfferProduct[];
  currentIndex: number;
  onSelect: (index: number) => void;
};

const SimplifiedOfferReviewProductCarouselTabs = ({
  sortedProducts,
  currentIndex,
  onSelect,
}: SimplifiedProductCarouselTabsProps) => {
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const titleScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = titleScrollRef.current;
    const activeTab = tabRefs.current[currentIndex];
    if (!scrollContainer || !activeTab) {
      return;
    }

    const nextScrollLeft = getTitleTabScrollLeft(scrollContainer, activeTab);

    if (nextScrollLeft === scrollContainer.scrollLeft) {
      return;
    }

    scrollContainer.scrollTo({
      left: nextScrollLeft,
      behavior: "smooth",
    });
  }, [currentIndex, sortedProducts.length]);

  if (sortedProducts.length === 0) {
    return null;
  }

  return (
    <TopNavButtons data-testid="simplified-product-carousel-tabs">
      <TopNavButtonsScroll ref={titleScrollRef}>
        {sortedProducts.map((product, index) => (
          <TabButtonShell
            key={`${product.productId ?? "product"}-${index}`}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
          >
            <ProductTabButton
              color="tertiary"
              size="sm"
              onClick={() => onSelect(index)}
              $isCurrent={index === currentIndex}
              aria-label={getSimplifiedProductTabLabel(product)}
              aria-current={index === currentIndex ? "true" : undefined}
            >
              {getSimplifiedProductTabLabel(product)}
            </ProductTabButton>
          </TabButtonShell>
        ))}
      </TopNavButtonsScroll>
    </TopNavButtons>
  );
};

type SimplifiedProductCarouselPanelProps = {
  sortedProducts: ReviewOfferProduct[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
};

const SimplifiedOfferReviewProductCarouselPanel = ({
  sortedProducts,
  currentIndex,
  onIndexChange,
}: SimplifiedProductCarouselPanelProps) => {
  const [showProductImageModal, setShowProductImageModal] = useState(false);

  if (sortedProducts.length === 0) {
    return null;
  }

  const currentProduct = sortedProducts[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === sortedProducts.length - 1;
  return (
    <PanelRoot>
      <FrostedPanel>
        <CarouselContent data-testid="simplified-product-carousel-image">
          <CarouselSlideTrack
            $count={sortedProducts.length}
            $index={currentIndex}
          >
            {sortedProducts.map((product, index) => (
              <CarouselSlide
                key={`slide-${product.productId}-${index}`}
                $count={sortedProducts.length}
              >
                <SlideInner>
                  <ProductImageFrame>
                    <CarouselImage
                      bgImageUrl={product.imageUrl}
                      role="img"
                      aria-hidden={index !== currentIndex}
                      aria-label={product.name}
                    />
                    <QuantityBlock>
                      <QuantityLabel size={12} color={tokens.BRAND_HERO_BLUE}>
                        Quantity
                      </QuantityLabel>
                      <QuantityValue size={16} color={tokens.BRAND_HERO_BLUE}>
                        {product.count}
                      </QuantityValue>
                    </QuantityBlock>
                  </ProductImageFrame>
                </SlideInner>
              </CarouselSlide>
            ))}
          </CarouselSlideTrack>
        </CarouselContent>
        <ExpandImageButton
          size="sm"
          color="primary"
          onClick={() => setShowProductImageModal(true)}
          aria-label={`View ${currentProduct.name}`}
          data-testid="simplified-product-carousel-expand"
          iconMiddle={
            <SvgIcon
              name={SvgNames.ArrowsOutSimple}
              width={18}
              height={18}
              color={tokens.WHITE}
            />
          }
        />
        {sortedProducts.length > 1 && (
          <>
            <CarouselArrowButton
              size="sm"
              color="tertiary"
              onClick={() => onIndexChange(currentIndex - 1)}
              disabled={isFirst}
              aria-label="Previous product"
              $position="left"
              iconMiddle={
                <SvgIcon
                  name={SvgNames.ChevronLeft}
                  width={24}
                  height={24}
                  color={tokens.BRAND_HERO_BLUE}
                />
              }
            />
            <CarouselArrowButton
              size="sm"
              color="tertiary"
              onClick={() => onIndexChange(currentIndex + 1)}
              disabled={isLast}
              aria-label="Next product"
              $position="right"
              iconMiddle={
                <SvgIcon
                  name={SvgNames.ChevronRight}
                  width={24}
                  height={24}
                  color={tokens.BRAND_HERO_BLUE}
                />
              }
            />
          </>
        )}
      </FrostedPanel>
      {showProductImageModal && (
        <ProductImageModal
          onClose={() => setShowProductImageModal(false)}
          testId="simplified-product-carousel-image-modal"
        >
          <ProductImageModalContainer>
            <ProductImageModalImage
              src={currentProduct.imageUrl}
              alt={currentProduct.name}
            />
          </ProductImageModalContainer>
        </ProductImageModal>
      )}
    </PanelRoot>
  );
};

const PanelRoot = styled.div`
  width: 100%;
  min-width: 0;
`;

const FrostedPanel = styled.div`
  position: relative;
  width: 100%;
  height: 440px;
  border-radius: 10px;
  background: color-mix(in srgb, ${tokens.WHITE} 30%, transparent);
  backdrop-filter: blur(8px);
  box-sizing: border-box;
  padding: 16px 16px 20px;
  overflow: hidden;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    --carousel-height-min: 336px;
    --carousel-height-max: 440px;
    --carousel-vw-min: 375px;
    --carousel-vw-max: 768px;
    --carousel-vw-range: 393px;
    --carousel-height-range: 104px;
    height: clamp(
      var(--carousel-height-min),
      calc(
        var(--carousel-height-min) + (100vw - var(--carousel-vw-min)) *
          var(--carousel-height-range) / var(--carousel-vw-range)
      ),
      var(--carousel-height-max)
    );
  }
`;

const TopNavButtons = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const TopNavButtonsScroll = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabButtonShell = styled.div`
  flex-shrink: 0;
  display: inline-flex;
`;

const ProductTabButton = styled(Button)<{ $isCurrent: boolean }>`
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  border-radius: 0;

  &.sr-button-tertiary {
    padding: 8px;
    border-bottom: ${({ $isCurrent }) =>
      $isCurrent
        ? `2px solid ${tokens.BRAND_HERO_BLUE}`
        : "2px solid transparent"};
  }
`;

const trackWidthPercent = (slideCount: number) => slideCount * 100;

const trackOffsetPercent = (activeIndex: number, slideCount: number) =>
  (-activeIndex / slideCount) * 100;

const slideFlexBasisPercent = (slideCount: number) => 100 / slideCount;

const CarouselContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const CarouselSlideTrack = styled.div<{ $count: number; $index: number }>`
  display: flex;
  height: 100%;
  width: ${({ $count }) => trackWidthPercent($count)}%;
  transform: translateX(
    ${({ $index, $count }) => trackOffsetPercent($index, $count)}%
  );
  transition: transform 0.38s cubic-bezier(0.33, 1, 0.68, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CarouselSlide = styled.div<{ $count: number }>`
  flex: 0 0 ${({ $count }) => slideFlexBasisPercent($count)}%;
  height: 100%;
  min-width: 0;
`;

const SlideInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`;

const ProductImageFrame = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const ExpandImageButton = styled(Button)`
  position: absolute;
  bottom: 14px;
  right: 14px;
  z-index: 1;
  width: 34px;
  height: 34px;
  box-shadow: 0 2px 8px color-mix(in srgb, ${tokens.BLACK} 15%, transparent);

  &.sr-button-primary {
    background: ${tokens.NIGHT_BLACK_HERO};
    border-radius: 50%;
    border: none;
  }
`;

const ProductImageModal = styled(Modal)`
  > .modal-container {
    height: 90%;
    width: 90%;
    overflow: scroll;

    > #modal-header {
      padding-bottom: 0px;
    }

    > .modal-content-container {
      max-height: none;
    }
  }
`;

const ProductImageModalContainer = styled.div`
  position: relative;
  width: 100%;
  height: 65vh;
  margin: auto;
  padding-top: 10px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.NIGHT_BLACK_HERO};
`;

const ProductImageModalImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const CarouselImage = styled.div<{ bgImageUrl: string }>`
  width: 100%;
  height: 100%;
  background: url(${({ bgImageUrl }) => bgImageUrl}) no-repeat center;
  background-size: contain;
  transform: scale(0.85);
`;

const QuantityBlock = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  pointer-events: none;
`;

const QuantityLabel = styled(Typography)`
  line-height: 140% !important;
`;

const QuantityValue = styled(Typography)`
  font-weight: 700 !important;
  line-height: 140% !important;
`;

const CarouselArrowButton = styled(Button)<{ $position: "left" | "right" }>`
  position: absolute;
  ${({ $position }) => ($position === "left" ? "left: 8px;" : "right: 8px;")}
  top: 55%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  z-index: 1;

  &.sr-button-tertiary {
    background: none;
    border: none;
    border-radius: 50%;
  }
`;

export {
  useSimplifiedProductCarousel,
  SimplifiedOfferReviewProductCarouselTabs,
  SimplifiedOfferReviewProductCarouselPanel,
};
